import type { GetWebcastSummaryManagerQuery, Video, VideoManager } from '../../../../../../generated/graphql-manager';

import {
  FileIcon,
  IconButton,
  LinkStyleButton,
  LockIcon,
  PencilIcon,
  Tooltip,
  TrashIcon,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { RestrictedBadge } from 'src/components/restricted-badge/restricted-badge';
import { VideoDetailsModal } from 'src/manager/components/video-manager-browser/video-details-modal';
import { VideoUrlModal } from 'src/manager/components/video-url-modal/video-url-modal';
import { useGetFeatures } from 'src/manager/hooks/use-get-features';
import { useGetVideo } from 'src/manager/hooks/use-get-video';
import { secondsToMMSS } from 'src/utils';

import { State, useUpdateVodMutation } from '../../../../../../generated/graphql-manager';
import { secondsToHHMMSS } from '../../../../../../utils';
import { VideoBrowser } from '../../../../../components/video-manager-browser/video-browser';
import { useVmproConnection } from '../../../../../providers/vmpro-connector';

import styles from './event-summary-vod.module.css';

type EventSummaryVodProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  language: string;
  onVideoSelect: () => void;
};

type VideoStructuredData = {
  language: string;
  title: string;
  duration: number;
  thumbnailUrl: string;
  offsetInSeconds: number;
  channelId: string;
  createdDate: string;
  id: string;
  videoManagerId: string;
};

const VIDEO_MANAGER_OPEN_STATE = 'VIDEO_MANAGER_OPEN_STATE';

export function EventSummaryVod({ webcast, language, onVideoSelect }: EventSummaryVodProps) {
  const { t } = useTranslation();
  const { isVmproLinked, saveState } = useVmproConnection();
  const [searchParams] = useSearchParams();
  const { isVideoVodManifestUrlEnabled } = useGetFeatures();
  const { video, videoError, onGetVideoData, onClearVideoError } = useGetVideo();

  const [videoStructuredData, setVideoStructuredData] = useState<VideoStructuredData>();
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [chosenLanguage, setChosenLanguage] = useState<string>();
  const [currentVodVideo, setCurrentVodVideo] = useState<{ video: VideoStructuredData; language: string }>();
  const [videoSourceSelectionModalOpen, setVideoSourceSelectionModalOpen] = useState(false);

  const [updateVodMutation, { loading }] = useUpdateVodMutation();

  const webcastId = webcast.id;
  const streaming = webcast.streaming;
  const vodVideos = streaming?.vod?.streams;
  const videoId = vodVideos?.find((vodVideo) => vodVideo.metadata?.language === language)?.reference?.videoId;
  const videoManagerId = vodVideos?.find((vodVideo) => vodVideo.metadata?.language === language)?.reference
    ?.videoManagerId;
  const videoVodManifestUrl = isVideoVodManifestUrlEnabled
    ? vodVideos?.find((vod) => vod.metadata?.language === language)?.videoVodManifestUrl
    : undefined;

  const videoIdRef = useRef(videoId);
  const videoManagerIdRef = useRef(videoManagerId);
  const onGetVideoDataRef = useRef(onGetVideoData);

  const uploadChannels = webcast?.recordingSettings?.videoManager?.channels;
  const uploadVideoManagerId = webcast?.recordingSettings?.videoManager?.videoManagerId;
  const archivedWebcast = webcast?.state === State.ARCHIVED;
  const securityPolicyEnabled = video?.securityPolicy?.__typename === 'VideoManagerSecurityPolicy';
  const unauthorizedAccess = videoError === 'UNAUTHORIZED_ACCESS';
  const vmproLinkMissing = videoError === 'VMPRO_LINK_MISSING';
  const vmproVideoProblem = unauthorizedAccess || vmproLinkMissing;

  useEffect(() => {
    const previousState = searchParams.get('state');

    if (!previousState) return;

    if (previousState === VIDEO_MANAGER_OPEN_STATE && isVmproLinked) {
      if (videoIdRef.current && videoManagerIdRef.current) {
        onGetVideoDataRef.current(videoManagerIdRef.current, videoIdRef.current);
      } else {
        setFileBrowserOpen(true);
      }
    }
  }, [searchParams, isVmproLinked]);

  useEffect(() => {
    if (videoId && videoManagerId) onGetVideoData(videoManagerId, videoId);
  }, [language, onGetVideoData, videoId, videoManagerId]);

  useEffect(() => {
    if (video && vodVideos && videoId) {
      const vodVideo = vodVideos.find((vodVideo) => vodVideo.metadata?.language === language);

      if (!vodVideo) return;

      const { reference, metadata, offsetInSeconds } = vodVideo;
      const { id, channelId, title, thumbnail, duration, createdDate } = video;

      const videoData = {
        id,
        title,
        channelId,
        createdDate,
        thumbnailUrl: thumbnail || '',
        offsetInSeconds: offsetInSeconds || 0,
        duration: duration || 0,
        videoManagerId: reference?.videoManagerId || '',
        language: metadata?.language || '',
      };

      setVideoStructuredData(videoData);
    } else {
      setVideoStructuredData(undefined);
    }
  }, [language, video, videoId, vodVideos]);

  const setVodVideo = (
    { id: videoId, channelId }: Video,
    videoManagerId?: VideoManager['id'],
    offsetInSeconds?: number
  ) => {
    if (!chosenLanguage) return;

    setFileBrowserOpen(false);

    updateVodMutation({
      variables: {
        input: {
          webcastId,
          language: chosenLanguage,
          offsetInSeconds: offsetInSeconds || 0,
          video: {
            channelId,
            videoId,
            videoManagerId,
          },
          videoVodManifestUrl: { url: null },
        },
      },
      onCompleted: (data) => {
        if (data.updateVod.__typename !== 'UpdateVodProblem') onClearVideoError();
        onVideoSelect();
      },
    });
  };

  const toggleFileBrowserVisibility = (language?: string) => {
    setChosenLanguage(language);
    setFileBrowserOpen(Boolean(language));
    if (!isVmproLinked) saveState(VIDEO_MANAGER_OPEN_STATE);
  };

  const toggleSourceSelectionModalVisibility = (language?: string) => {
    setChosenLanguage(language);
    setVideoSourceSelectionModalOpen(Boolean(language));
  };

  const onSaveVideoVodManifestUrl = (url: string | null) => {
    updateVodMutation({
      variables: {
        input: {
          webcastId,
          language,
          offsetInSeconds: 0,
          video: null,
          videoVodManifestUrl: { url },
        },
      },
      onCompleted: () => {
        toggleSourceSelectionModalVisibility();
        onVideoSelect();
      },
    });
  };

  const onConfirm = (video: Video, videoManagerId?: VideoManager['id'], offsetInSeconds?: number) => {
    if (offsetInSeconds !== undefined && currentVodVideo) {
      updateVodMutation({
        variables: {
          input: {
            webcastId,
            language: currentVodVideo.language,
            offsetInSeconds,
            video: {
              channelId: video.channelId,
              videoId: video.id,
              videoManagerId,
            },
            videoVodManifestUrl: { url: null },
          },
        },
        onCompleted: onVideoSelect,
      });
    }
  };

  const clearVideoId = () => setCurrentVodVideo(undefined);

  return (
    <>
      {videoVodManifestUrl && (
        <div data-testid={'custom-hls-url'} className={styles.videoVodManifestUrl}>
          <span className={styles.url}>{videoVodManifestUrl}</span>

          <div>
            <Tooltip label={t('manager.webcastSetup.media.videoVodManifestUrl.edit')}>
              <IconButton
                data-testid={'edit-custom-hls-url-button'}
                className={styles.iconButton}
                rounded
                aria-label={t('manager.webcastSetup.media.videoVodManifestUrl.edit')}
                onClick={() => toggleSourceSelectionModalVisibility(language)}
              >
                <PencilIcon />
              </IconButton>
            </Tooltip>

            <Tooltip label={t('manager.webcastSetup.media.videoVodManifestUrl.remove')}>
              <IconButton
                data-testid={'remove-custom-hls-url-button'}
                className={styles.iconButton}
                rounded
                aria-label={t('manager.webcastSetup.media.videoVodManifestUrl.remove')}
                onClick={() => onSaveVideoVodManifestUrl(null)}
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      {!videoVodManifestUrl && (
        <>
          {videoStructuredData && (
            <span
              data-testid="selected-video-thumbnail"
              className={classNames(styles.videoContainer, styles.videoThumbnail)}
              style={{ backgroundImage: `url('${videoStructuredData.thumbnailUrl}')` }}
            >
              <span className={styles.time}>
                {videoStructuredData.duration
                  ? secondsToHHMMSS(videoStructuredData.duration / 1000 - videoStructuredData.offsetInSeconds)
                  : undefined}
              </span>
            </span>
          )}

          {vmproVideoProblem ? (
            <div className={classNames(styles.videoContainer, styles.missingAccess)} data-testid="vmpro-video-problem">
              <LockIcon />
              {unauthorizedAccess && t('manager.eventSummary.videoOnDemand.unauthorizedAccess')}
              {vmproLinkMissing && t('manager.eventSummary.videoOnDemand.linkToVmproMissing')}
            </div>
          ) : (
            !videoStructuredData && (
              <button
                type="button"
                data-testid="select-video-button"
                className={classNames(styles.videoContainer, styles.selectVideoButton)}
                disabled={archivedWebcast}
                onClick={() =>
                  isVideoVodManifestUrlEnabled
                    ? toggleSourceSelectionModalVisibility(language)
                    : toggleFileBrowserVisibility(language)
                }
              >
                <FileIcon />
                {t('manager.eventSummary.videoOnDemand.selectVideoButton')}
              </button>
            )
          )}

          <div className={styles.selectedVodContainer}>
            {videoStructuredData && (
              <>
                <div className={styles.title}>
                  <Tooltip label={videoStructuredData?.title}>
                    <div className={styles.selectedVodName} data-testid="selected-video-name">
                      {videoStructuredData?.title}
                    </div>
                  </Tooltip>

                  {securityPolicyEnabled && <RestrictedBadge />}
                </div>

                <div data-testid="time-label" className={styles.timeLabel}>
                  {videoStructuredData.offsetInSeconds
                    ? t(`manager.eventSummary.videoOnDemand.startTime.time`, {
                        time: secondsToMMSS(videoStructuredData.offsetInSeconds),
                      })
                    : ''}

                  <LinkStyleButton
                    data-testid="edit-start-time-button"
                    className={styles.selectVideo}
                    onClick={() => setCurrentVodVideo({ video: videoStructuredData, language: language })}
                  >
                    {videoStructuredData.offsetInSeconds
                      ? t('manager.eventSummary.videoOnDemand.startTime.editButton')
                      : t('manager.eventSummary.videoOnDemand.startTime.adjustButton')}
                  </LinkStyleButton>
                </div>

                <LinkStyleButton
                  data-testid="change-video-button"
                  className={styles.selectVideo}
                  disabled={archivedWebcast}
                  onClick={() =>
                    isVideoVodManifestUrlEnabled
                      ? toggleSourceSelectionModalVisibility(language)
                      : toggleFileBrowserVisibility(language)
                  }
                >
                  {t('manager.eventSummary.videoOnDemand.changeVideoButton')}
                </LinkStyleButton>
              </>
            )}

            {vmproVideoProblem && (
              <>
                <div className={styles.title}>
                  <Tooltip label={videoId}>
                    <div className={styles.selectedVodName} data-testid="selected-video-id">
                      {t('manager.eventSummary.videoOnDemand.videoId')}
                      {videoId}
                    </div>
                  </Tooltip>

                  {securityPolicyEnabled && <RestrictedBadge />}
                </div>

                {unauthorizedAccess && (
                  <LinkStyleButton
                    data-testid="change-video-button"
                    className={styles.selectVideo}
                    onClick={() => toggleFileBrowserVisibility(language)}
                    disabled={archivedWebcast}
                  >
                    {t('manager.eventSummary.videoOnDemand.changeVideoButton')}
                  </LinkStyleButton>
                )}

                {vmproLinkMissing && (
                  <LinkStyleButton
                    data-testid="link-to-vmpro-button"
                    className={styles.selectVideo}
                    onClick={() => toggleFileBrowserVisibility(language)}
                    disabled={archivedWebcast}
                  >
                    {t('manager.eventSummary.videoOnDemand.linkToVmpro')}
                  </LinkStyleButton>
                )}
              </>
            )}
          </div>
        </>
      )}

      <VideoBrowser
        isOpen={fileBrowserOpen}
        title={t('manager.eventSummary.videoOnDemand.videoBrowserTitle')}
        videoDetailsTitle={t('manager.eventSummary.videoOnDemand.videoDetailsModalTitle')}
        onChooseVideo={(...data) => {
          setVodVideo(...data);
          setVideoSourceSelectionModalOpen(false);
        }}
        initialChannelId={uploadChannels?.[0]?.id}
        initialVideoManagerId={uploadVideoManagerId}
        enableOffsetSelection
        onClose={() => {
          toggleFileBrowserVisibility();
          setVideoSourceSelectionModalOpen(false);
        }}
      />

      <VideoDetailsModal
        isOpen={currentVodVideo !== undefined}
        title={t('manager.eventSummary.videoOnDemand.videoDetailsModalTitle')}
        videoId={currentVodVideo?.video.id || ''}
        videoManagerId={currentVodVideo?.video.videoManagerId}
        offsetInSeconds={currentVodVideo?.video.offsetInSeconds}
        enableOffsetSelection
        onConfirm={onConfirm}
        onClose={clearVideoId}
      />

      <VideoUrlModal
        isOpen={videoSourceSelectionModalOpen}
        loading={loading}
        initialVideoVodManifestUrl={videoVodManifestUrl}
        onSelectVideoFromVmpro={() => toggleFileBrowserVisibility(language)}
        onSaveVideoVodManifestUrl={onSaveVideoVodManifestUrl}
        onClose={() => toggleSourceSelectionModalVisibility(undefined)}
      />
    </>
  );
}
